import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ChevronRight, ChevronLeft, CalendarClock, Zap, Share2, Send, X, CheckCircle2, Settings2 } from "lucide-react";
import { AutopilotPanel } from "./AutopilotPanel";

const FLOW = ["draft", "ready", "approved", "scheduled", "published"];
const SOCIAL = ["linkedin", "facebook", "instagram"];
const COL = {
  draft: "border-slate-400", ready: "border-blue-500", approved: "border-violet-500",
  scheduled: "border-amber-500", published: "border-emerald-500",
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};
// ISO (UTC) -> value for <input type=datetime-local> in local time
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d - off).toISOString().slice(0, 16);
};

export default function PublishingQueue() {
  const [items, setItems] = useState(null);
  const [bufferChannels, setBufferChannels] = useState(null); // null=unloaded
  const [bufferFor, setBufferFor] = useState(null); // item id with open panel
  const [bufferChannel, setBufferChannel] = useState("");
  const [bufferWhen, setBufferWhen] = useState("");
  const [bufferBusy, setBufferBusy] = useState(false);
  const [bufferStatus, setBufferStatus] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfgAuto, setCfgAuto] = useState(false);
  const [cfgDefaults, setCfgDefaults] = useState({});
  const [cfgSaving, setCfgSaving] = useState(false);

  const load = () => api.get("/ops/content").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/ops/buffer/status")
      .then((r) => { setBufferStatus(r.data); setCfgAuto(!!r.data.auto_enabled); setCfgDefaults(r.data.defaults || {}); })
      .catch(() => setBufferStatus({ connected: false }));
  }, []);

  const loadChannels = async () => {
    if (bufferChannels !== null) return bufferChannels;
    try {
      const { data } = await api.get("/ops/buffer/channels");
      const allowed = (data.channels || []).filter((c) => SOCIAL.includes((c.service || "").toLowerCase()));
      setBufferChannels(allowed);
      return allowed;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not reach Buffer.";
      toast.error(typeof msg === "string" ? msg : "Could not reach Buffer.");
      return null;
    }
  };

  const openBuffer = async (item) => {
    setBufferFor(item.id); setBufferChannel(""); setBufferWhen("");
    const ch = await loadChannels();
    if (ch === null) { setBufferFor(null); return; }
    if (ch.length === 0) toast.message("No social channels connected in Buffer yet.");
  };

  const openCfg = async () => {
    setCfgOpen(true);
    await loadChannels();
  };

  const saveCfg = async () => {
    setCfgSaving(true);
    try {
      const { data } = await api.put("/ops/buffer/settings", { auto_enabled: cfgAuto, defaults: cfgDefaults });
      setBufferStatus((s) => ({ ...(s || {}), auto_enabled: data.auto_enabled, defaults: data.defaults }));
      toast.success("Auto-post settings saved");
      setCfgOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally { setCfgSaving(false); }
  };

  const sendToBuffer = async (item) => {
    if (!bufferChannel) { toast.error("Pick a channel"); return; }
    if (!bufferWhen) { toast.error("Pick a date & time"); return; }
    setBufferBusy(true);
    try {
      const iso = new Date(bufferWhen).toISOString();
      const { data } = await api.post("/ops/buffer/schedule", {
        content_id: item.id, channel_id: bufferChannel, when: iso,
      });
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, buffer: {
        post_id: data.post?.id, status: data.post?.status, channel_id: bufferChannel, due_at: data.post?.dueAt || iso,
      } } : x)));
      toast.success("Scheduled to your Buffer queue");
      setBufferFor(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Buffer scheduling failed.";
      toast.error(typeof msg === "string" ? msg : "Buffer scheduling failed.");
    } finally { setBufferBusy(false); }
  };

  const move = async (item, dir) => {
    const i = FLOW.indexOf(item.status);
    const ni = i + dir;
    if (ni < 0 || ni >= FLOW.length) return;
    const next = FLOW[ni];
    setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, status: next } : x)));
    try {
      await api.patch(`/ops/content/${item.id}/stage`, null, { params: { status: next } });
      toast.success(`Moved to ${next}`);
    } catch { toast.error("Move failed"); load(); }
  };

  const schedule = async (item, localValue) => {
    if (!localValue) return;
    const iso = new Date(localValue).toISOString();
    try {
      const { data } = await api.patch(`/ops/content/${item.id}/schedule`, null, { params: { when: iso } });
      setItems((prev) => prev.map((x) => (x.id === item.id ? data : x)));
      toast.success(`Scheduled for ${fmtWhen(iso)}`);
    } catch { toast.error("Could not schedule"); load(); }
  };

  if (!items) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;

  const byStage = (s) => items.filter((i) => i.status === s);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Publishing Queue</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Move each piece through the flow. Set a date on <span className="font-semibold">Approved/Scheduled</span> cards and it <span className="font-semibold">auto-publishes itself</span> — blogs go live automatically.</p>
        </div>
        <button onClick={openCfg} data-testid="buffer-cfg-open"
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm font-semibold hover:border-indigo-400 transition-colors whitespace-nowrap">
          <Settings2 className="w-4 h-4" />
          Auto-post to Buffer
          <span className={`ml-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${bufferStatus?.auto_enabled ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-slate-100 text-slate-500 dark:bg-slate-800"}`}>
            {bufferStatus?.auto_enabled ? "ON" : "OFF"}
          </span>
        </button>
      </div>

      {cfgOpen && (
        <div className="rounded-xl border border-indigo-200 dark:border-indigo-900 bg-indigo-50/50 dark:bg-indigo-950/30 p-5" data-testid="buffer-cfg-panel">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-lg">Auto-post approved social posts to Buffer</h2>
            <button onClick={() => setCfgOpen(false)} data-testid="buffer-cfg-close" className="p-1 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          {!bufferStatus?.connected ? (
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-3">Buffer isn't connected in this environment yet. Set <code className="font-mono">BUFFER_API_KEY</code> and redeploy to activate.</p>
          ) : (
            <>
              <label className="flex items-center gap-2 mt-4 text-sm font-medium cursor-pointer" data-testid="buffer-auto-toggle">
                <input type="checkbox" checked={cfgAuto} onChange={(e) => setCfgAuto(e.target.checked)} className="w-4 h-4 accent-indigo-600" />
                When a LinkedIn / Facebook / Instagram post is moved to <span className="font-semibold">Approved</span>, add it to my Buffer queue's next open slot automatically.
              </label>
              <div className="grid sm:grid-cols-3 gap-3 mt-4">
                {SOCIAL.map((svc) => {
                  const opts = (bufferChannels || []).filter((c) => (c.service || "").toLowerCase() === svc);
                  return (
                    <div key={svc}>
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 capitalize">{svc} channel</div>
                      {opts.length === 0 ? (
                        <p className="text-xs text-slate-400 mt-1">No {svc} channel connected in Buffer.</p>
                      ) : (
                        <select value={cfgDefaults[svc] || ""} onChange={(e) => setCfgDefaults((d) => ({ ...d, [svc]: e.target.value }))}
                          data-testid={`buffer-default-${svc}`}
                          className="w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5 text-sm">
                          <option value="">— none —</option>
                          {opts.map((c) => <option key={c.id} value={c.id}>{c.displayName || c.name}</option>)}
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>
              <button onClick={saveCfg} disabled={cfgSaving} data-testid="buffer-cfg-save"
                className="mt-4 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
                {cfgSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save settings
              </button>
            </>
          )}
        </div>
      )}

      <AutopilotPanel onChange={load} />

      <div className="grid md:grid-cols-3 xl:grid-cols-5 gap-4">
        {FLOW.map((stage) => {
          const col = byStage(stage);
          return (
            <div key={stage} data-testid={`queue-col-${stage}`} className={`rounded-xl bg-white dark:bg-slate-900 border-t-2 ${COL[stage]} border-x border-b border-slate-200 dark:border-slate-800`}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                <span className="font-heading font-bold text-sm capitalize">{stage}</span>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800">{col.length}</span>
              </div>
              <div className="p-3 space-y-2 min-h-[80px]">
                {col.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">Empty</p>
                ) : col.map((it) => {
                  const idx = FLOW.indexOf(stage);
                  const showSchedule = stage === "approved" || stage === "scheduled";
                  const isDue = it.scheduled_at && new Date(it.scheduled_at) <= new Date();
                  return (
                    <div key={it.id} data-testid={`queue-card-${it.id}`} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 p-3">
                      <div className="text-sm font-medium leading-snug line-clamp-2">{it.title}</div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 capitalize">{it.type}</div>

                      {stage === "scheduled" && it.scheduled_at && (
                        <div className="flex items-center gap-1 mt-2 text-[11px] font-semibold text-amber-600 dark:text-amber-400" data-testid={`queue-sched-badge-${it.id}`}>
                          <Zap className="w-3 h-3" /> {isDue ? "Publishing…" : `Auto-publishes ${fmtWhen(it.scheduled_at)}`}
                        </div>
                      )}

                      {showSchedule && (
                        <label className="flex items-center gap-1.5 mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                          <CalendarClock className="w-3.5 h-3.5 shrink-0" />
                          <input
                            type="datetime-local"
                            defaultValue={toLocalInput(it.scheduled_at)}
                            onChange={(e) => schedule(it, e.target.value)}
                            data-testid={`queue-schedule-${it.id}`}
                            className="flex-1 min-w-0 bg-transparent border border-slate-200 dark:border-slate-700 rounded px-1.5 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        </label>
                      )}

                      {SOCIAL.includes((it.type || "").toLowerCase()) && (
                        <div className="mt-2">
                          {it.buffer?.post_id ? (
                            <div className="flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400" data-testid={`buffer-sent-${it.id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5" /> In Buffer queue
                            </div>
                          ) : bufferFor === it.id ? (
                            <div className="rounded-md border border-indigo-200 dark:border-indigo-900 bg-indigo-50/60 dark:bg-indigo-950/40 p-2 space-y-1.5" data-testid={`buffer-panel-${it.id}`}>
                              {bufferChannels === null ? (
                                <div className="flex items-center gap-1 text-[11px] text-slate-500"><Loader2 className="w-3 h-3 animate-spin" /> Loading channels…</div>
                              ) : (
                                <>
                                  <select value={bufferChannel} onChange={(e) => setBufferChannel(e.target.value)} data-testid={`buffer-channel-${it.id}`}
                                    className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-1 text-[11px]">
                                    <option value="">Choose channel…</option>
                                    {bufferChannels.map((c) => (
                                      <option key={c.id} value={c.id}>{c.displayName || c.name} ({c.service})</option>
                                    ))}
                                  </select>
                                  <input type="datetime-local" value={bufferWhen} onChange={(e) => setBufferWhen(e.target.value)} data-testid={`buffer-when-${it.id}`}
                                    className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-1 text-[11px]" />
                                  <div className="flex items-center gap-1.5">
                                    <button disabled={bufferBusy} onClick={() => sendToBuffer(it)} data-testid={`buffer-send-${it.id}`}
                                      className="flex-1 inline-flex items-center justify-center gap-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded px-2 py-1 text-[11px] font-semibold disabled:opacity-50">
                                      {bufferBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Schedule
                                    </button>
                                    <button onClick={() => setBufferFor(null)} data-testid={`buffer-cancel-${it.id}`}
                                      className="p-1 rounded text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
                                  </div>
                                </>
                              )}
                            </div>
                          ) : (
                            <button onClick={() => openBuffer(it)} data-testid={`buffer-open-${it.id}`}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-500">
                              <Share2 className="w-3.5 h-3.5" /> Send to Buffer
                            </button>
                          )}
                        </div>
                      )}

                      <div className="flex justify-between mt-2">
                        <button disabled={idx === 0} onClick={() => move(it, -1)} data-testid={`queue-back-${it.id}`}
                          className="p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-20"><ChevronLeft className="w-4 h-4" /></button>
                        <button disabled={idx === FLOW.length - 1} onClick={() => move(it, 1)} data-testid={`queue-fwd-${it.id}`}
                          className="p-1 rounded text-indigo-500 hover:text-indigo-400 disabled:opacity-20"><ChevronRight className="w-4 h-4" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

